import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const VIEW_AS_KEY = "fb_view_as";
const VALID_VIEW_ROLES = ["admin", "registration", "bar"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAsState] = useState(() => {
    if (typeof window === "undefined") return null;
    const v = localStorage.getItem(VIEW_AS_KEY);
    return VALID_VIEW_ROLES.includes(v) ? v : null;
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("fb_token");
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        if (token) localStorage.removeItem("fb_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("fb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("fb_token");
    localStorage.removeItem(VIEW_AS_KEY);
    setViewAsState(null);
    setUser(null);
  };

  const refreshMe = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  const setViewAs = (role) => {
    // Only admins can impersonate; non-admins silently ignored.
    if (user?.role !== "admin") return;
    if (role && !VALID_VIEW_ROLES.includes(role)) return;
    if (role) localStorage.setItem(VIEW_AS_KEY, role);
    else localStorage.removeItem(VIEW_AS_KEY);
    setViewAsState(role || null);
  };

  // Effective role: admin viewing-as other role gets the other role's nav/landing;
  // actual permissions on the backend remain admin.
  const effectiveRole = user?.role === "admin" && viewAs ? viewAs : user?.role;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe, setUser, viewAs, setViewAs, effectiveRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
